import { IUserLoginInfo } from 'redhotpie-shared-lib/interfaces/IUser';
import { APPROVAL_STATUS } from '../constants/constants';
import * as utils from '../functions/utils';
import friends from './friends';
import { IFriend } from 'redhotpie-shared-lib/interfaces/IFriendUser';
import { IDateFinderDetail } from 'redhotpie-shared-lib/interfaces/IDateFinder';

interface DateFinderObject {
  ID: string;
  DateHeld: string;
  ShortComment: string;
  Comment: string;
  Location: string;
  City: string;
  StateName: string;
  StateNameAbbr: string;
  CountryName: string;
  AgeMin: number;
  AgeMax: number;
  SeekingTypes: string;
  HasExpressedInterest: boolean;
  OpenForList: any[];
  OpenForTypes: string;
  SeekingTypeList: any[];
  IsCommentRejected: boolean;
  IsCommentVisible: boolean;
  InterestCount: number;
  Profile: any;
  CanHost: boolean;
}

interface InterestDetail {
  DateCreated: string;
  ID: number;
  IsHidden: boolean;
  IsMessageSent: boolean;
}

interface SearchCriteria {
  AgeMin: number;
  AgeMax: number;
  GoogleMapPlaceID: string;
  SearchRadius: number;
  SeekingTypes: string;
  WithPhoto: boolean;
}

interface CountSummary {
  TotalDateMatchingCount: number;
  TotalDates: number;
}

export default class DateFinder {
  id: number;
  dateHeld: string;
  comment: string;
  shortComment: string;
  location: string;
  city: string;
  stateName: string;
  stateNameAbbr: string;
  countryName: string;
  ageMin: number;
  ageMax: number;
  seekingTypes: string;
  openForList: any[];
  openForTypes: string;
  seekingTypeList: any[];
  hasExpressedInterest: boolean;
  profile: IFriend;
  isCommentRejected: boolean;
  isCommentVisible: boolean;
  interestCount: number;
  approvalStatus: string | undefined;
  canHost: boolean;

  constructor() {
    this.id = -1;
    this.dateHeld = '';
    this.comment = '';
    this.shortComment = '';
    this.location = '';
    this.city = '';
    this.stateName = '';
    this.stateNameAbbr = '';
    this.countryName = '';
    this.ageMin = 18;
    this.ageMax = 99;
    this.seekingTypes = '';
    this.openForList = [];
    this.openForTypes = '';
    this.seekingTypeList = [];
    this.hasExpressedInterest = false;
    this.profile = {};
    this.isCommentRejected = false;
    this.isCommentVisible = false;
    this.interestCount = 0;
    this.approvalStatus = undefined;
    this.canHost = false;
  }

  static mappingPropertyObject(object: any) {
    const newObject: any = {};
    newObject.value = object.Value || '';
    newObject.text = object.Text || '';
    newObject.textAbbr = object.TextAbbr || '';
    newObject.isMatch = object.IsMatch || false;
    return newObject;
  }

  static mappingObject(_datefinder: DateFinderObject): IDateFinderDetail {
    if (_datefinder) {
      let datefinder = Object.assign(new DateFinder(), _datefinder);
      datefinder.id = Number(_datefinder.ID || '-1');
      datefinder.dateHeld = utils.extractDateToUnix(_datefinder.DateHeld);
      datefinder.shortComment = _datefinder.ShortComment;
      datefinder.comment = _datefinder.Comment;
      datefinder.location = _datefinder.Location;
      datefinder.city = _datefinder.City;
      datefinder.stateName = _datefinder.StateName;
      datefinder.stateNameAbbr = _datefinder.StateNameAbbr;
      datefinder.countryName = _datefinder.CountryName;
      datefinder.ageMin = _datefinder.AgeMin ? _datefinder.AgeMin : 18;
      datefinder.ageMax = _datefinder.AgeMax ? _datefinder.AgeMax : 99;
      datefinder.seekingTypes = _datefinder.SeekingTypes ? _datefinder.SeekingTypes : '';
      datefinder.hasExpressedInterest = _datefinder.HasExpressedInterest ? _datefinder.HasExpressedInterest : false;
      datefinder.openForList = _datefinder.OpenForList ? _datefinder.OpenForList : [];
      datefinder.openForTypes = _datefinder.OpenForTypes ? _datefinder.OpenForTypes : '';
      datefinder.seekingTypeList = _datefinder.SeekingTypeList
        ? _datefinder.SeekingTypeList.map(item => DateFinder.mappingPropertyObject(item))
        : [];
      datefinder.isCommentRejected = _datefinder.IsCommentRejected ? _datefinder.IsCommentRejected : false;
      datefinder.isCommentVisible = _datefinder.IsCommentVisible ? _datefinder.IsCommentVisible : false;
      datefinder.interestCount = _datefinder.InterestCount ? _datefinder.InterestCount : 0;
      datefinder.profile = _datefinder.Profile ? friends.mappingObject(_datefinder.Profile) : {};
      datefinder.approvalStatus =
        !datefinder.isCommentVisible && !datefinder.isCommentRejected
          ? APPROVAL_STATUS.PENDING
          : datefinder.isCommentRejected
            ? APPROVAL_STATUS.REJECTED
            : undefined;
      datefinder.canHost = _datefinder.CanHost;
      datefinder = utils.cleanObjectKeys(Object.keys(new DateFinder()), datefinder); // clean other key doesn't exist in user model

      return datefinder;
    }
    return new DateFinder();
  }

  static mappingInterestDetail(_datefinder: { InterestDetail: InterestDetail; Profile: any }, isProfile: boolean) {
    const tempStr = isProfile ? 'profile' : 'user';
    const obj: any = {
      dateCreated: '',
      id: 0,
      isHidden: false,
      isMessageSent: false,
      [tempStr]: {},
    };
    obj.dateCreated = _datefinder.InterestDetail.DateCreated ? _datefinder.InterestDetail.DateCreated : '';
    obj.id = _datefinder.InterestDetail.ID ? _datefinder.InterestDetail.ID : 0;
    obj.isHidden = _datefinder.InterestDetail.IsHidden ? _datefinder.InterestDetail.IsHidden : false;
    obj.isMessageSent = _datefinder.InterestDetail.IsMessageSent ? _datefinder.InterestDetail.IsMessageSent : false;
    obj[tempStr] = _datefinder.Profile ? friends.mappingObject(_datefinder.Profile) : {};
    return obj;
  }

  static mappingObjectsInterest(_datefinders: any[], isProfile: boolean) {
    const datefinders: any[] = [];
    _datefinders.forEach(_datefinder => {
      const datefinder = DateFinder.mappingInterestDetail(_datefinder, isProfile);
      datefinders.push(datefinder);
    });
    return datefinders;
  }

  static mappingObjects(_datefinders: DateFinderObject[]): IDateFinderDetail[] {
    const datefinders: IDateFinderDetail[] = [];
    _datefinders.forEach(_datefinder => {
      const datefinder = DateFinder.mappingObject(_datefinder);
      datefinders.push(datefinder);
    });
    return datefinders;
  }

  static mappingSearchCriteria(criteria: SearchCriteria) {
    const searchCriteria: any = {};
    if (criteria) {
      searchCriteria.ageMin = criteria.AgeMin;
      searchCriteria.ageMax = criteria.AgeMax;
      searchCriteria.googleMapPlaceID = criteria.GoogleMapPlaceID;
      searchCriteria.searchRadius = criteria.SearchRadius;
      searchCriteria.seekingTypes = criteria.SeekingTypes;
      searchCriteria.withPhoto = criteria.WithPhoto;
    }
    return searchCriteria;
  }

  static mappingDataCountSummary(summary: CountSummary) {
    const _summary: any = {};
    if (summary) {
      _summary.totalDateMatchingCount = summary.TotalDateMatchingCount;
      _summary.totalDates = summary.TotalDates;
    }
    return _summary;
  }
}
